import { useCallback, useEffect, useState } from 'react';
import { Dropdown, DefaultDropdownProps, Option } from 'framework/form';
import useExpertiseService from '../../service/expertiseService';
import { useFilterContext } from 'framework/list';
import { FilterCondition } from 'framework/types';
import { usePositionService, PositionType } from 'domain/shared/employee';
import { ExpertiseSuggestionsDto } from '../../service/expertiseService.types';

const ExpertiseDropdown = (props: Omit<DefaultDropdownProps, 'options'>) => {
  const { findExpertiseSuggestions } = useExpertiseService();
  const [options, setOptions] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expertiseItems, setExpertiseItems] = useState<ExpertiseSuggestionsDto | null>(null);
  const { getValue } = useFilterContext();
  const { findPositionById } = usePositionService();
  const positionIds = getValue('position.id', FilterCondition.IN) as unknown as string[] | null;

  const filterOptionsByPositions = useCallback(async () => {
    if (!expertiseItems) {
      return;
    }

    const selectedPositionAliases: string[] = [];
    const hasSelectedPositions = !!positionIds?.length;

    if (hasSelectedPositions) {
      for (const id of positionIds) {
        const position: PositionType = await findPositionById(Number(id));
        selectedPositionAliases.push(position.alias);
      }
    }

    const data = expertiseItems.result.reduce((arr, exp) => {
      if (hasSelectedPositions) {
        const aliases = exp.allowedPositionAliases;
        const isAliasSelected = aliases.some(a => selectedPositionAliases.includes(a));
        if (isAliasSelected) {
          arr.push({
            value: exp.id,
            label: exp.displayName,
          });
        }
      } else {
        arr.push({
          value: exp.id,
          label: exp.displayName,
        });
      }

      return arr;
    }, [] as Option[]);

    setOptions(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expertiseItems, positionIds]);

  useEffect(() => {
    findExpertiseSuggestions({
      searchRequest: { pageNumber: 0, pageSize: 9999 },
    })
      .then(items => {
        setExpertiseItems(items);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterOptionsByPositions();
  }, [filterOptionsByPositions]);

  return (
    <Dropdown placeholder="Select expertise" {...props} options={options} hasFilterByLabel isLoading={isLoading} />
  );
};

export default ExpertiseDropdown;
